import React from "react";
import watchwicketLogo from "../../assets/file_00000000711072438e9d72dabae9eda2.png";
import { useAuth } from "../../contexts/AuthContext";
import { NavScreen } from "../BottomNav";

interface AppShellProps {
  navScreen: NavScreen;
  onNavigate: (screen: NavScreen) => void;
  onOpenMenu: () => void;
  onOpenProfile: () => void;
  onOpenSettings: () => void;
  onOpenAccount?: () => void;
  children: React.ReactNode;
}

interface NavDef {
  screen: NavScreen;
  label: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavDef[] = [
  {
    screen: "home",
    label: "Home",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
    ),
  },
  {
    screen: "myMatches",
    label: "My Matches",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    ),
  },
  {
    screen: "players",
    label: "Player Profiles",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    ),
  },
  {
    screen: "leagues",
    label: "Leagues",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 3v4a3 3 0 003 3h0M19 3v4a3 3 0 01-3 3h0M9 21h6M12 17v4M8 3h8v5a4 4 0 01-8 0V3z" />
    ),
  },
  {
    screen: "support",
    label: "Support",
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.3 7.3L3 21l1.7-6.7A8 8 0 1121 12z" />
    ),
  },
];

export default function AppShell({
  navScreen,
  onNavigate,
  onOpenMenu,
  onOpenProfile,
  onOpenSettings,
  onOpenAccount,
  children,
}: AppShellProps) {
  const { user, profile } = useAuth();
  const displayName = profile?.full_name || user?.email?.split("@")[0] || "Player";
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <div className="lg:pl-64">
      <aside
        className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 z-40"
        style={{
          background: "linear-gradient(to bottom, #06281D, #031B13)",
          borderRight: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <div className="flex items-center gap-3 px-5 h-16" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <img src={watchwicketLogo} alt="WatchWicket" className="w-10 h-10 object-contain" />
          <div className="flex flex-col">
            <span className="text-[#F5F7F6] text-[15px] font-bold tracking-tight leading-none">WatchWicket</span>
            <span className="text-[#9FB6AC]/50 text-[9px] font-semibold tracking-[0.1em] uppercase leading-none mt-0.5">Live Cricket Scoring</span>
          </div>
        </div>

        <div className="px-3 py-4">
          <button
            onClick={() => onNavigate("newMatch")}
            className="w-full flex items-center justify-center gap-2 bg-[#00C980] hover:bg-[#19E69B] text-[#031B13] font-bold py-3 rounded-xl shadow-lg transition-colors"
            style={{ boxShadow: "0 4px 16px rgba(0,201,128,0.25)" }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            New Match
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 space-y-1">
          {NAV_ITEMS.slice(0, 2).map((item) => {
            const active = navScreen === item.screen;
            return (
              <button
                key={item.screen}
                onClick={() => onNavigate(item.screen)}
                aria-current={active ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  active
                    ? "text-[#F5F7F6]"
                    : "text-[#9FB6AC]/60 hover:text-[#F5F7F6] hover:bg-white/[0.05]"
                }`}
                style={active ? { background: "rgba(0,201,128,0.12)" } : undefined}
              >
                <span
                  className="w-1 h-5 rounded-full transition-colors"
                  style={{ background: active ? "#00C980" : "transparent" }}
                  aria-hidden="true"
                />
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {item.icon}
                </svg>
                <span>{item.label}</span>
              </button>
            );
          })}

          <div className="pt-3 pb-1 px-3">
            <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#9FB6AC]/40">Teams & Squads</span>
          </div>
          {([
            { screen: "teamsIndoor" as NavScreen, label: "Indoor" },
            { screen: "teamsT20" as NavScreen, label: "T20" },
            { screen: "teamsODI" as NavScreen, label: "ODI" },
          ]).map((item) => {
            const active = navScreen === item.screen;
            return (
              <button
                key={item.screen}
                onClick={() => onNavigate(item.screen)}
                aria-current={active ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  active
                    ? "text-[#F5F7F6]"
                    : "text-[#9FB6AC]/60 hover:text-[#F5F7F6] hover:bg-white/[0.05]"
                }`}
                style={active ? { background: "rgba(0,201,128,0.12)" } : undefined}
              >
                <span
                  className="w-1 h-5 rounded-full transition-colors"
                  style={{ background: active ? "#00C980" : "transparent" }}
                  aria-hidden="true"
                />
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span>{item.label}</span>
              </button>
            );
          })}

          <div className="pt-2" />
          {NAV_ITEMS.slice(2).map((item) => {
            const active = navScreen === item.screen;
            return (
              <button
                key={item.screen}
                onClick={() => onNavigate(item.screen)}
                aria-current={active ? "page" : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  active
                    ? "text-[#F5F7F6]"
                    : "text-[#9FB6AC]/60 hover:text-[#F5F7F6] hover:bg-white/[0.05]"
                }`}
                style={active ? { background: "rgba(0,201,128,0.12)" } : undefined}
              >
                <span
                  className="w-1 h-5 rounded-full transition-colors"
                  style={{ background: active ? "#00C980" : "transparent" }}
                  aria-hidden="true"
                />
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {item.icon}
                </svg>
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 space-y-1" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <button
            onClick={onOpenAccount || onOpenProfile}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/[0.05] transition-colors text-left"
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
              style={{ background: "rgba(0,201,128,0.15)", border: "1px solid rgba(0,201,128,0.2)", color: "#00C980" }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[#F5F7F6] text-sm font-semibold truncate">{displayName}</p>
              <p className="text-[#9FB6AC]/40 text-xs truncate">View profile</p>
            </div>
          </button>
          <div className="flex gap-1">
            <button
              onClick={onOpenSettings}
              className="flex-1 flex items-center justify-center gap-2 px-2 py-2 rounded-lg text-[#9FB6AC]/60 hover:text-[#F5F7F6] hover:bg-white/[0.05] transition-colors text-xs font-semibold"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>
            <button
              onClick={onOpenMenu}
              className="flex-1 flex items-center justify-center gap-2 px-2 py-2 rounded-lg text-[#9FB6AC]/60 hover:text-[#F5F7F6] hover:bg-white/[0.05] transition-colors text-xs font-semibold"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              More
            </button>
          </div>
        </div>
      </aside>

      {children}
    </div>
  );
}
